import HistoryRoundedIcon from '@mui/icons-material/HistoryRounded'
import VisibilityRoundedIcon from '@mui/icons-material/VisibilityRounded'
import { Box, Button, CircularProgress, Typography } from '@mui/material'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useIntl } from 'react-intl'
import { useMe } from '../../../app/hooks/useMe'
import { useEndActiveImpersonationSessionMutation } from '../../../app/redux-fetch/apiImpersonation'
import { clearImpersonationSession, clearPendingStart } from './impersonationSessionStorage'
import SessionActivityDrawer from './SessionActivityDrawer'
import messages from './messages'

// E11 R2 (WI 615) — the persistent impersonation banner. Mounted app-wide (AppShell, inside PersonaGate) so
// it is visible on every authenticated route while a session is live. It reads useMe().impersonation, which
// the E9 middleware Stage 4 populates only once a session is started — so the banner is inert (renders null)
// until the E11 cutover. It is rendered as a FIXED BOTTOM strip rather than a top bar deliberately: App uses
// an `AppBar position='fixed'` at the top with a Toolbar spacer, so a top banner would collide with the nav;
// a bottom strip needs zero layout surgery on App and is the conventional impersonation-exit-bar placement.
//
// Ending impersonation (Stop button, or auto-end when the countdown hits zero) calls the caller's own
// active/end endpoint, then hard-reloads to the app root: ending flips the whole request identity
// (persona/tenant), so all RTK/axios cache fetched AS the impersonated user must be purged, not surgically
// invalidated. The client auto-end is a UX convenience only — the server independently enforces expiry (the
// daily timer + the per-request Stage-4 grant check), so a backgrounded tab that ends late is still safe.

// The return-to-self side effect, extracted so tests can inject a spy instead of touching window.location.
export const returnToSelf = () => window.location.assign('/')

interface ImpersonationBannerProps {
  // Overridable only for testing; production always uses the full-reload returnToSelf.
  onEnded?: () => void
}

const pad = (n: number) => String(n).padStart(2, '0')

// Format a remaining-milliseconds value as a compact countdown: "1:02:59" with hours, "12:07" under an hour.
// Clamped at zero so a slightly-negative remaining (past expiry, pre-auto-end) never renders a minus sign.
const formatRemaining = (remainingMs: number): string => {
  const totalSeconds = Math.max(0, Math.floor(remainingMs / 1000))
  const hours = Math.floor(totalSeconds / 3600)
  const minutes = Math.floor((totalSeconds % 3600) / 60)
  const seconds = totalSeconds % 60
  return hours > 0 ? `${hours}:${pad(minutes)}:${pad(seconds)}` : `${minutes}:${pad(seconds)}`
}

const ImpersonationBanner = ({ onEnded = returnToSelf }: ImpersonationBannerProps) => {
  const { formatMessage } = useIntl()
  const { impersonation } = useMe()
  const [endSession, { isLoading }] = useEndActiveImpersonationSessionMutation()

  const [remainingMs, setRemainingMs] = useState<number | null>(null)
  const [activityOpen, setActivityOpen] = useState(false)
  // One-shot guard so the countdown-hits-zero auto-end fires exactly once per session (not every tick), and
  // so a failed auto-end doesn't retry-storm active/end + its error toast. Reset when the session changes.
  const autoEndFiredRef = useRef(false)

  // Primitive deps (not the impersonation object) so the ticker effect can't re-fire on RTK re-render churn.
  const sessionId = impersonation?.sessionId ?? null
  const expiresAt = impersonation?.expiresAt ?? null

  useEffect(() => {
    autoEndFiredRef.current = false
  }, [sessionId])

  useEffect(() => {
    if (expiresAt == null) {
      setRemainingMs(null)
      return
    }
    const expiryMs = new Date(expiresAt).getTime()
    // Recompute from the absolute expiry each tick, so background-tab throttling causes at most a display lag
    // (never drift) — a foregrounded tab shows the true remaining within one second.
    const tick = () => setRemainingMs(expiryMs - Date.now())
    tick()
    const intervalId = window.setInterval(tick, 1000)
    const onVisible = () => {
      if (document.visibilityState === 'visible') tick()
    }
    document.addEventListener('visibilitychange', onVisible)
    return () => {
      window.clearInterval(intervalId)
      document.removeEventListener('visibilitychange', onVisible)
    }
  }, [expiresAt])

  const endNow = useCallback(async () => {
    try {
      await endSession().unwrap()
      // Clear the client session state BEFORE the reload so the post-end app carries no stale
      // X-LogikSpark-Impersonation-Session-Id header (a request under an ended session id would 403 in
      // Stage 4). endSession() already sent this final request, so clearing now is safe.
      clearImpersonationSession()
      clearPendingStart()
      onEnded()
    } catch {
      // The shared base query already toasted the failure; stay mounted so the user can retry via the button.
      // (Re-arm auto-end is intentionally NOT done here — see autoEndFiredRef.)
    }
  }, [endSession, onEnded])

  useEffect(() => {
    if (expiresAt != null && remainingMs !== null && remainingMs <= 0 && !autoEndFiredRef.current) {
      autoEndFiredRef.current = true
      void endNow()
    }
  }, [expiresAt, remainingMs, endNow])

  if (!impersonation) {
    return null
  }

  return (
    <>
    <Box
      // role='status' announces the "you are impersonating X" region once when it appears; the per-second
      // countdown below is aria-hidden so it does not re-announce every tick (expiry is server-enforced).
      role='status'
      aria-label='impersonation-banner'
      sx={{
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        zIndex: (theme) => theme.zIndex.drawer + 2,
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        px: 2,
        py: 1,
        bgcolor: 'warning.main',
        color: 'warning.contrastText',
        boxShadow: 6,
      }}
    >
      <VisibilityRoundedIcon fontSize='small' />
      <Typography variant='body2' component='div' sx={{ flexGrow: 1 }}>
        <Box component='span'>
          {formatMessage(messages.bannerText, {
            name: impersonation.targetDisplayName,
            email: impersonation.targetEmail,
            tenant: impersonation.targetTenantName,
          })}
        </Box>
        {remainingMs !== null && (
          <Box component='span' aria-hidden sx={{ fontWeight: 700, ml: 1 }}>
            {formatMessage(messages.bannerEndsIn, { time: formatRemaining(remainingMs) })}
          </Box>
        )}
      </Typography>
      <Button
        size='small'
        variant='text'
        color='inherit'
        onClick={() => setActivityOpen(true)}
        startIcon={<HistoryRoundedIcon />}
      >
        {formatMessage(messages.activityLink)}
      </Button>
      <Button
        size='small'
        variant='contained'
        color='inherit'
        onClick={endNow}
        disabled={isLoading}
        startIcon={isLoading ? <CircularProgress size={14} color='inherit' /> : undefined}
      >
        {formatMessage(messages.bannerStop)}
      </Button>
    </Box>
    <SessionActivityDrawer
      sessionId={impersonation.sessionId}
      open={activityOpen}
      onClose={() => setActivityOpen(false)}
    />
    </>
  )
}

export default ImpersonationBanner
